import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for persisting form data to localStorage
 * Preserves form data on page refresh and restores it when the page loads
 *
 * @param {string} key - Unique key for storing form data in localStorage
 * @param {object} initialState - Initial form state
 * @param {object} options - Additional options
 * @param {number} options.debounceMs - Debounce delay for saving (default: 300ms)
 * @param {boolean} options.warnOnUnload - Show warning before page unload if form has data (default: true)
 * @returns {[object, function, function, boolean]} - [formData, setFormData, clearFormData, hasRestoredData]
 */
export function useFormPersistence(key, initialState = {}, options = {}) {
  const { debounceMs = 300, warnOnUnload = true } = options;

  // State to track if data was restored from localStorage
  const [hasRestoredData, setHasRestoredData] = useState(false);

  // Initialize state from localStorage or use initial state
  const [formData, setFormDataInternal] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if stored data has non-empty values
        const hasData = Object.values(parsed).some(value =>
          value !== '' && value !== null && value !== undefined
        );
        if (hasData) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error reading form data from localStorage:', error);
    }
    return initialState;
  });

  // Check if form has unsaved data (non-empty values)
  const hasFormData = useCallback(() => {
    return Object.entries(formData).some(([key, value]) => {
      // Skip checking certain fields that might be auto-populated
      if (key === 'reason') return false; // reason is optional
      return value !== '' && value !== null && value !== undefined;
    });
  }, [formData]);

  // Effect to detect if data was restored on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        const hasData = Object.values(parsed).some(value =>
          value !== '' && value !== null && value !== undefined
        );
        setHasRestoredData(hasData);
      }
    } catch (error) {
      // Ignore errors
    }
  }, [key]);

  // Save to localStorage with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(formData));
      } catch (error) {
        console.error('Error saving form data to localStorage:', error);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [formData, key, debounceMs]);

  // Add beforeunload warning if form has data
  useEffect(() => {
    if (!warnOnUnload) return;

    const handleBeforeUnload = (e) => {
      if (hasFormData()) {
        e.preventDefault();
        e.returnValue = 'You have unsaved form data. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasFormData, warnOnUnload]);

  // Update form data (merge with existing)
  const setFormData = useCallback((updater) => {
    setFormDataInternal(prev => {
      if (typeof updater === 'function') {
        return updater(prev);
      }
      return { ...prev, ...updater };
    });
    // Clear restored data flag when user starts typing
    setHasRestoredData(false);
  }, []);

  // Clear form data from state and localStorage
  const clearFormData = useCallback(() => {
    setFormDataInternal(initialState);
    setHasRestoredData(false);
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error clearing form data from localStorage:', error);
    }
  }, [key, initialState]);

  return [formData, setFormData, clearFormData, hasRestoredData];
}

/**
 * Hook to specifically persist booking form data with doctor/service context
 *
 * @param {string} doctorId - The doctor ID for context
 * @returns {object} - Object with form data and handlers
 */
export function useBookingFormPersistence(doctorId) {
  const storageKey = `booking_form_${doctorId || 'guest'}`;

  const [formData, setFormData, clearFormData, hasRestoredData] = useFormPersistence(
    storageKey,
    {
      name: '',
      email: '',
      phone: '',
      reason: ''
    },
    { debounceMs: 300, warnOnUnload: true }
  );

  // Update a single field
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData]);

  return {
    formData,
    updateField,
    setFormData,
    clearFormData,
    hasRestoredData
  };
}

export default useFormPersistence;
